import * as actions from './actionTypes'
import { LOG_OUT } from 'ducks/auth'
import {
  filterValues,
  isDeepEqual,
  mergeDeepRight,
  updatePath,
} from 'utils/object'

const STATUS = {
  ok: 'ok',
  patching: 'patching',
  fetching: 'fetching',
  deleting: 'deleting',
  deleted: 'deleted',
  error: 'error',
}

// Template state for model
const baseModelState = { items: {}, meta: { selected: {} } }

// initialize state by passing an array of model names, representings sub
// slices
// [str] -> {str: {k:v}}
export const initialOrmState = keys =>
  Object.fromEntries(keys.map(key => [key, baseModelState]))

// makeReducer :: (state, {type: state=>state}) -> (state, {type}) -> state
const makeReducer =
  (initialState = {}, actionMapping = {}) =>
  action =>
  (state = initialState) => {
    const handler = actionMapping[action.type]
    return handler ? handler(action)(state) : state
  }

// reducer for meta
const metaReducer = makeReducer(baseModelState.meta, {
  [actions.TOGGLE_SELECT]:
    ({ payload }) =>
    meta =>
      updatePath(meta, ['selected', payload], selected => !selected),
  [actions.SET_SELECTION]:
    ({ payload }) =>
    meta => ({ ...meta, selected: payload }),
  [actions.RESET_META]: () => () => baseModelState.meta,
})

// helper functions
const setStatus = status => item => ({ ...item, __status: status })

export const updateItem = data => item => ({
  ...item,
  __dirty: { ...item.__dirty, ...data },
})

// remove dirty props that are identical to the pristine
export const cleanDirty = item => ({
  ...item,
  __dirty: filterValues(
    item.__dirty ?? {},
    (value, key) => !isDeepEqual(value, item[key]),
  ),
})

// trims whitespace from all dirty string model instance properties. Throws
// when the item has no `__dirty`, as the original code did.
const trimWhiteSpace = item => ({
  ...item,
  __dirty: Object.fromEntries(
    Object.entries(item.__dirty).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.trim() : value,
    ]),
  ),
})

// set error value, typically a validation error from the rest api
const setError = payload => item => ({
  ...item,
  __error: payload.error,
  __status: payload.error ? STATUS.error : STATUS.ok,
})

// merge action payload (from patch/get) into store model instance
const mergePayload = payload => item => {
  const { __meta, ...data } = payload
  return setError(payload)(cleanDirty({ ...item, ...data }))
}

// reducer for single model entry
const reduceItem = ({ type, payload }, item) => {
  switch (type) {
    case actions.CHANGE:
      return cleanDirty(updateItem(payload)(item))
    case actions.RESET:
      return setError({})({ ...item, __dirty: payload })
    case actions.FETCH_START:
      return setStatus(STATUS.fetching)(item)
    case actions.DELETE_START:
      return setStatus(STATUS.deleting)(item)
    case actions.PATCH_START:
    case actions.POST_START:
      return setStatus(STATUS.patching)(trimWhiteSpace(item))
    case actions.FETCH_SUCCESS:
    case actions.POST_SUCCESS:
    case actions.PATCH_SUCCESS:
      return mergePayload(payload)(item)
    case actions.PATCH_ERROR:
    case actions.POST_ERROR:
      return setError(payload)(item)
    case actions.DELETE_SUCCESS:
      return { __status: STATUS.deleted }
    default:
      return item
  }
}

// base reducer for orm
export const reducer = (state: any = {}, action: any = {}) => {
  const { meta: { model, pk } = {}, payload } = action
  if (action.type === LOG_OUT)
    // clear orm cache on log out
    return {}

  if (action.type === actions.PENDING_REQUEST) {
    return mergeDeepRight(state, action.payload)
  }

  if (!model)
    // all valid orm actions have `meta.model` property
    // ignore actions without `meta.model`
    return state
  if (pk) {
    // if there's a `pk` property, the action is for a single item
    const next = updatePath(state, [model, 'items', `${pk}`], (item = {}) =>
      reduceItem(action, item),
    )
    return action.type === actions.DELETE_SUCCESS
      ? updatePath(next, [model, 'order'], (order: unknown[]) =>
          order.filter(id => id !== pk),
        )
      : next
  }
  const results = payload?.results
  if (results) {
    const count = payload.count
    const order = results.map(result => String(result.id))

    // TODO: this is hacky. Should be done in a more elegant way
    // Received multiple results from the api
    // create actions and recurse for each item in results
    const slice = results
      .map(result => ({
        type: action.type,
        meta: { model, pk: result.id },
        payload: result,
      }))
      .reduce((acc, itemAction) => reducer(acc, itemAction), {
        [model]: { count: null, items: {}, meta: { selected: {} } },
      })

    return { ...state, [model]: { ...slice[model], count, order } }
  }
  // other actions are passed to the meta reducer for that model slice

  return updatePath(state, [model, 'meta'], metaReducer(action))
}
